import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { LeadAttributeApiService } from '../services/lead-attribute-api.service';
import { LeadAttributeRequest, LeadAttributeResponse, LeadAttributeValueType } from '../models/lead-attribute.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-lead-attribute-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './lead-attribute-form.component.html'
})
export class LeadAttributeFormComponent implements OnInit {
  saving = false;
  error = '';
  loading = false;
  isEditing = false;
  attributeId: number | null = null;

  duplicateNameError = '';
  attributeNameValidationError = '';
  displayNameValidationError = '';

  readonly NAME_MAX = 30;

  attributeName = '';
  displayName = '';
  private originalDisplayName = '';
  valueType: LeadAttributeValueType = 'STRING';

  private existingAttributes: LeadAttributeResponse[] = [];

  readonly valueTypeOptions: { value: LeadAttributeValueType; label: string }[] = [
    { value: 'STRING', label: 'Texto (STRING)' },
    { value: 'NUMBER', label: 'Número (NUMBER)' },
    { value: 'BOOLEAN', label: 'Booleano (BOOLEAN)' },
    { value: 'DATE', label: 'Fecha (DATE)' },
    { value: 'FK', label: 'Referencia (FK)' }
  ];

  constructor(
    private attributeApi: LeadAttributeApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.attributeId = +id;
      this.cargarAtributosExistentes();
      this.cargarAtributo(this.attributeId);
    } else {
      this.cargarAtributosExistentes();
    }
  }

  private cargarAtributo(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.attributeApi.getAttributeById(id, businessId).subscribe({
      next: (atributo) => {
        this.attributeName = atributo.attributeName;
        this.displayName = atributo.displayName;
        this.originalDisplayName = atributo.displayName;
        this.valueType = atributo.valueType;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el atributo';
        this.loading = false;
      }
    });
  }

  private cargarAtributosExistentes(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.attributeApi.getAttributes(businessId).subscribe({
      next: (data) => {
        this.existingAttributes = data;
      },
      error: () => {
      }
    });
  }

  onAttributeNameChange(value: string): void {
    let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    if (filtered.length > this.NAME_MAX) {
      filtered = filtered.substring(0, this.NAME_MAX);
      this.attributeNameValidationError = 'Máximo 30 caracteres';
    } else if (filtered !== value) {
      this.attributeNameValidationError = 'Solo se permiten letras';
    } else {
      this.attributeNameValidationError = '';
    }
    setTimeout(() => { this.attributeName = filtered; });
  }

  onDisplayNameChange(value: string): void {
    let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    if (filtered.length > this.NAME_MAX) {
      filtered = filtered.substring(0, this.NAME_MAX);
      this.displayNameValidationError = 'Máximo 30 caracteres';
    } else if (filtered !== value) {
      this.displayNameValidationError = 'Solo se permiten letras';
    } else {
      this.displayNameValidationError = '';
    }
    setTimeout(() => { this.displayName = filtered; });
  }

  private esNombreDuplicado(nombre: string): boolean {
    const target = nombre.trim().toLowerCase();
    return this.existingAttributes.some(a =>
      a.attributeName.trim().toLowerCase() === target &&
      (!this.isEditing || a.id !== this.attributeId)
    );
  }

  private esEtiquetaDuplicada(etiqueta: string): boolean {
    const target = etiqueta.trim().toLowerCase();
    return this.existingAttributes.some(a =>
      a.displayName.trim().toLowerCase() === target &&
      (!this.isEditing || a.id !== this.attributeId)
    );
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Atributo de Lead' : 'Nuevo Atributo de Lead';
  }

  guardar(): void {
    const faltantes: string[] = [];
    if (!this.attributeName.trim()) faltantes.push('Nombre de campo');
    if (!this.displayName.trim()) faltantes.push('Etiqueta');
    if (!this.valueType) faltantes.push('Tipo de valor');

    if (faltantes.length > 0) {
      this.error = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.error = '';
    this.duplicateNameError = '';
    this.attributeNameValidationError = '';
    this.displayNameValidationError = '';

    if (this.esNombreDuplicado(this.attributeName)) {
      this.duplicateNameError = `Ya existe un atributo con el nombre "${this.attributeName.trim()}"`;
      this.toast.warning({ title: 'Nombre duplicado', message: this.duplicateNameError });
      return;
    }

    if (this.esEtiquetaDuplicada(this.displayName)) {
      this.duplicateNameError = `Ya existe un atributo con la etiqueta "${this.displayName.trim()}"`;
      this.toast.warning({ title: 'Etiqueta duplicada', message: this.duplicateNameError });
      return;
    }

    this.saving = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: LeadAttributeRequest = {
      businessId,
      attributeName: this.attributeName.trim(),
      displayName: this.displayName.trim(),
      valueType: this.valueType
    };

    if (this.isEditing && this.attributeId) {
      this.attributeApi.updateAttribute(this.attributeId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Atributo de lead',
            this.originalDisplayName !== this.displayName.trim()
              ? `Se actualizó "${this.originalDisplayName}" → "${this.displayName.trim()}".`
              : `Se actualizó "${this.displayName.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/atributos']);
        },
        error: (err) => {
          this.saving = false;
          // Inc 36: 409 = nombre duplicado → mensaje claro por campo.
          if (err.status === 409) {
            const msg = err.error?.message || `Ya existe otro atributo de lead con el nombre "${this.attributeName.trim()}"`;
            this.duplicateNameError = msg;
            this.toast.error({ title: 'Nombre duplicado', message: msg });
            return;
          }
          this.error = err.error?.message || 'Error al actualizar el atributo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.attributeApi.createAttribute(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Atributo de lead', `Se creó "${this.displayName.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/atributos']);
        },
        error: (err) => {
          this.saving = false;
          if (err.status === 409) {
            const msg = err.error?.message || `Ya existe otro atributo de lead con el nombre "${this.attributeName.trim()}"`;
            this.duplicateNameError = msg;
            this.toast.error({ title: 'Nombre duplicado', message: msg });
            return;
          }
          this.error = err.error?.message || 'Error al crear el atributo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-leads/atributos']);
  }
}
